import TagInputRow from "./SupervisorTagMinutes/TagInputRow";
import TagsGrid from "./SupervisorTagMinutes/TagsGrid";
import {
  getHasSupervisorContext,
  getNewFormState,
  getAllocationTotalNumMinutes,
  HubletTag,
} from "./supervisorContextUtils";
import type { AllocationObj, TimeRangeFinite } from "types/types";
import { Button } from "@mui/material";
import { useState } from "react";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { Add } from "@mui/icons-material";
import { useBreakpoints } from "constants/constants";
import { TagsGridModal } from "./SupervisorTagMinutes/TagsGridModal";

type SupervisorContextFormContentProps = {
  formState: AllocationObj[];
  setFormState: React.Dispatch<React.SetStateAction<AllocationObj[]>>;
  unknownMinutes: number;
  isEditable: boolean;
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
  isAboveOrEqualToMinuteLimit: boolean;
  hubletTags: HubletTag[];
  timeRange: TimeRangeFinite;
  processId: string;
};

export default function SupervisorContextFormContent({
  formState,
  setFormState,
  unknownMinutes,
  isEditable,
  setIsEditable,
  isAboveOrEqualToMinuteLimit,
  hubletTags,
  timeRange,
  processId,
}: SupervisorContextFormContentProps) {
  const addTagMinuteInput = (category: string, tagName: string) => {
    setFormState(
      getNewFormState({
        category,
        tagName,
        newMinutes: 0,
        timeRange,
        processId,
        formState,
      })
    );
    setIsEditable(true);
  };

  const removeTagMinuteInput = (category: string, tagName: string) => {
    const newFormState = getNewFormState({
      category,
      tagName,
      newMinutes: 0,
      timeRange,
      processId,
      formState,
    });
    setFormState(newFormState);
  };

  const getHandleInputNumberChange =
    (category: string, tagName: string) =>
    (event: { target: { value: string }; keyCode: number }) => {
      let newNumMinutes = parseInt(event.target.value, 10);
      if (isNaN(newNumMinutes)) {
        newNumMinutes = 0;
      }

      const newFormState = getNewFormState({
        category,
        tagName,
        newMinutes: newNumMinutes,
        timeRange,
        processId,
        formState,
      });

      // check unknownMinutes limit
      // prevent keycode 69 (e) on number inputs in chrome
      // because "technically e is a number 🤓"
      // https://stackoverflow.com/questions/31706611/why-does-the-html-input-with-type-number-allow-the-letter-e-to-be-entered-in/31706796
      if (
        getAllocationTotalNumMinutes(newFormState) <= unknownMinutes ||
        event.keyCode === 69
      ) {
        setFormState(newFormState);
      }
    };

  // Increase or decrease number of minutes button press
  const handleArrowClick = (tagName: string, increase: boolean) => {
    const tagInfo = hubletTags.find(x => x.tagName === tagName);
    const category = tagInfo?.category || "";

    const minutesToAdd = increase ? 1 : -1;
    const formStateObj = formState.find(x => x.tagName === tagName);
    const newMinutes = (formStateObj?.numberOfMinutes || 0) + minutesToAdd;
    const newFormState = getNewFormState({
      category,
      tagName,
      newMinutes,
      timeRange,
      processId,
      formState,
    });
    setFormState(newFormState);
  };

  const getHandleClickCheckbox = tagName => e => {
    // find index of formState obj we are changing within formState array
    const formStateObjIndex = formState.findIndex(x => x.tagName === tagName);
    const formStateObj = formState[formStateObjIndex];

    // flip state of changeover boolean
    const newFormStateObject = {
      ...formStateObj,
      isChangeover: e.target.checked,
    };

    // replace the original object in formState arr
    const newFormState = [
      ...formState.slice(0, formStateObjIndex),
      newFormStateObject,
      ...formState.slice(formStateObjIndex + 1),
    ];
    setFormState(newFormState);
  };

  const hasSupervisorContext = getHasSupervisorContext(formState);
  const shouldShowGrid = isEditable || !hasSupervisorContext;
  const { isTabletOrLarger } = useBreakpoints();

  const handleFinalTagClick = (tag: HubletTag) =>
    addTagMinuteInput(tag.category, tag.tagName);

  return (
    <>
      {formState.length > 0 &&
        formState.map(
          ({ category, tagName, numberOfMinutes, isChangeover }) => {
            return (
              <TagInputRow
                key={tagName}
                name={tagName}
                value={numberOfMinutes}
                handleOnChange={getHandleInputNumberChange(category, tagName)}
                disabled={!isEditable}
                handleArrowClick={handleArrowClick}
                disableIncrease={isAboveOrEqualToMinuteLimit}
                disableDecrease={numberOfMinutes === 0}
                removeTagMinuteInput={() =>
                  removeTagMinuteInput(category, tagName)
                }
                checked={isChangeover}
                handleCheckClick={getHandleClickCheckbox(tagName)}
              />
            );
          }
        )}
      {shouldShowGrid &&
        (isTabletOrLarger ? (
          <TagsGrid
            {...{
              formState,
              handleFinalTagClick,
              hubletTags,
            }}
          />
        ) : (
          <ButtonOpenTagsGrid
            {...{
              formState,
              handleFinalTagClick,
              hubletTags,
            }}
          />
        ))}
    </>
  );
}

function ButtonOpenTagsGrid({
  formState,
  handleFinalTagClick,
  hubletTags,
}: {
  formState?: any[] | AllocationObj[];
  handleFinalTagClick: (tag: HubletTag) => void;
  hubletTags: HubletTag[];
}) {
  const [isTagsGridOpen, setIsTagsGridOpen] = useState(false);
  const { translate } = useFlightTranslation();
  return (
    <>
      <Button
        variant="outlined"
        onClick={() => {
          setIsTagsGridOpen(true);
        }}
        startIcon={<Add />}
        style={{ textTransform: "none", marginBottom: 12 }}
      >
        {translate("Add_a_Tag")}
      </Button>
      <TagsGridModal
        {...{
          formState,
          handleFinalTagClick,
          hubletTags,
          isTagsGridOpen,
          setIsTagsGridOpen,
        }}
      />
    </>
  );
}
